import React, { useState } from 'react';
import { Search } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { useSearchHistory } from '../../context/SearchHistoryContext';
import { FloatingChat } from './components/FloatingChat';
import SearchResultsPage from './pages/SearchResultsPage';
import { Eyebrow, PageHeading } from '../../design-system';
import styles from './SearchPage.module.css';

interface SearchPageProps {
  hideHeader?: boolean;
}

const SearchPage: React.FC<SearchPageProps> = ({ hideHeader = true }) => {
  // Defaults to hidden: /search moved inside the app shell, so Chat, Dashboard,
  // History and Logout are all in the sidebar beside it now and this row was a
  // second copy of them. A caller that embeds the page without a shell can
  // still ask for it with hideHeader={false}.
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const { logout } = useAuth();
  const { searchHistory, addToSearchHistory } = useSearchHistory();
  const [isChatOpen, setIsChatOpen] = useState(false);

  const pathname = location?.pathname ?? '/search';
  const locationSearch = location?.search ?? '';
  const recentSearches = (searchHistory ?? []).map(item => item.query).slice(0, 4);
  const activeQuery = new URLSearchParams(locationSearch).get('q')?.trim();
  const embeddedSearchPath = (
    pathname.startsWith('/admin/dashboard') ||
    pathname.startsWith('/enterprise/dashboard') ||
    pathname.startsWith('/startup/dashboard') ||
    pathname.startsWith('/government/dashboard')
  ) ? pathname : null;

  const isEmbeddedSearch = Boolean(embeddedSearchPath);

  const buildResultsPath = (query: string) => {
    const encodedQuery = encodeURIComponent(query);
    return embeddedSearchPath
      ? `${embeddedSearchPath}?q=${encodedQuery}`
      : `/search/results?q=${encodedQuery}`;
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const query = searchQuery.trim();
    if (query) {
      addToSearchHistory(query);
      navigate(buildResultsPath(query));
    }
  };

  const handleRecentSearchClick = (query: string, e: React.MouseEvent) => {
    e.preventDefault();
    setSearchQuery(query);
    addToSearchHistory(query);
    navigate(buildResultsPath(query));
  };

  if (activeQuery) {
    return <SearchResultsPage />;
  }

  const toggleChat = () => {
    setIsChatOpen(!isChatOpen);
  };
  return (
    <div className={`${styles.searchPage} ${isEmbeddedSearch ? styles.searchPageEmbedded : ''}`}>
      <FloatingChat isOpen={isChatOpen} onToggle={toggleChat} />
      {/* Header */}
      {!hideHeader && (
        <header className={styles.header}>
          <div className={styles.topLeftLinks} />
          <div className={styles.topRightLinks}>
            <a href="/app" className={styles.topLink}>Chat</a>
            <a href="/dashboard" className={styles.topLink}>Dashboard</a>
            <a href="/search-history" className={styles.topLink}>History</a>
            <button onClick={logout} className={styles.logoutButton}>Logout</button>
          </div>
        </header>
      )}

      <div className={`${styles.searchContainer} ${isEmbeddedSearch ? styles.searchContainerEmbedded : ''}`}>
        {/* Policyora Logo */}
        <div className={styles.policyoraLogo}>
          <img
            src="/Policy Ora Final Logo.png"
            alt="Policyora"
            className={`${styles.policyoraLogoImg} ${theme === 'dark' ? styles.policyoraLogoDark : ''}`}
          />
        </div>

        <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
          <Eyebrow tone="default" dot>POLICY SEARCH</Eyebrow>
          <PageHeading
            accentWord="every"
            accentTone="orange"
            size="h2"
            align="center"
            level="h1"
          >
            Search across every regulation
          </PageHeading>
        </div>

        {/* Search Bar */}
        <div className={styles.googleSearchContainer}>
          <form
            onSubmit={handleSearch}
            className={styles.googleSearchForm}
            id="search-form"
            data-testid="search-form"
          >
            <div className={styles.searchIcon}><Search size={20} aria-hidden /></div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search AI Policies or related topics"
              className={styles.googleSearchInput}
              autoFocus
            />
            <div className={styles.searchActions}>
            </div>
          </form>
        </div>

        {/* Search Buttons */}
        <div className={styles.searchButtons}>
          {/* No buttons - just search on enter/submit */}
        </div>

        {/* Recent Searches */}
        {recentSearches.length > 0 && (
          <div className={styles.recentlySearches}>
            <h3>Recent Searches:</h3>
            <ul>
              {recentSearches.map((search, index) => (
                <li key={index}>
                  <button
                    type="button"
                    onClick={(e) => handleRecentSearchClick(search, e)}
                    className={styles.recentSearchLink}
                  >
                    {search}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export { SearchPage };
export default SearchPage;
